import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { users as usersApi, upload as uploadApi } from "../services/api";
import { TRAVEL_STYLES } from "../data/seedData";
import { ArrowLeft, Loader, Check, Camera } from "lucide-react";
import { useTranslation } from "react-i18next";

function EditProfile() {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const { t } = useTranslation();

  const STYLE_LABELS = {
    stays: t("editProfile.staysLabel"),
    eats: t("editProfile.eatsLabel"),
    group: t("editProfile.groupLabel"),
    interest: t("editProfile.interestLabel"),
    transport: t("editProfile.transportLabel"),
  };

  const [bio, setBio] = useState(user?.bio || "");
  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [selectedStyles, setSelectedStyles] = useState(user?.travelStyles || []);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatarUrl || null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const toggleStyle = (style) => {
    setSelectedStyles((prev) =>
      prev.includes(style) ? prev.filter((s) => s !== style) : [...prev, style]
    );
  };

  // Check at least one tag per category
  const missingCategories = Object.entries(TRAVEL_STYLES).filter(
    ([, styles]) => !styles.some((s) => selectedStyles.includes(s))
  ).map(([cat]) => STYLE_LABELS[cat]);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    setError(null);
    try {
      // Show local preview immediately
      setAvatarPreview(URL.createObjectURL(file));
      // Upload to Cloudinary
      const { avatarUrl } = await uploadApi.avatar(token, file);
      setAvatarPreview(avatarUrl);
      await refreshUser();
    } catch (err) {
      setError(err.message || t("editProfile.avatarFailed"));
      setAvatarPreview(user?.avatarUrl || null);
    }
    setAvatarUploading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await usersApi.update(token, user.id, {
        firstName,
        lastName,
        bio,
        travelStyles: selectedStyles,
      });
      await refreshUser();
      setSaved(true);
      setTimeout(() => navigate("/profile"), 800);
    } catch (err) {
      setError(err.message || t("editProfile.failedSave"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-th-bg">
      <div className="max-w-xl mx-auto">
        <div className="px-4 pt-[max(1rem,env(safe-area-inset-top))] mt-2">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 bg-th-surface border border-th-border text-th-text rounded-xl flex items-center justify-center"
            >
              <ArrowLeft size={16} />
            </button>
            <h1 className="text-lg font-bold text-th-text">{t("editProfile.title")}</h1>
          </div>
        </div>

        <div className="px-4 mt-6 space-y-5 pb-8">
          {/* Avatar */}
          <div className="flex justify-center">
            <label className="relative cursor-pointer group">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleAvatarChange}
                className="hidden"
              />
              <div className="w-24 h-24 rounded-2xl overflow-hidden border-2 border-th-border shadow-lg shadow-black/20">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-th-surface flex items-center justify-center text-3xl text-th-muted font-mono font-bold">
                    {user?.username?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
              </div>
              <div className="absolute inset-0 rounded-2xl bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                {avatarUploading ? (
                  <Loader size={20} className="animate-spin text-white" />
                ) : (
                  <Camera size={20} className="text-white" />
                )}
              </div>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-sm font-medium text-th-text mb-1.5">{t("editProfile.firstName")}</p>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-3 py-2.5 bg-th-surface border border-th-border rounded-xl text-base text-th-text placeholder-th-muted focus:outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all"
              />
            </div>
            <div>
              <p className="text-sm font-medium text-th-text mb-1.5">{t("editProfile.lastName")}</p>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-3 py-2.5 bg-th-surface border border-th-border rounded-xl text-base text-th-text placeholder-th-muted focus:outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all"
              />
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-th-text mb-1.5">{t("editProfile.bio")}</p>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder={t("editProfile.bioPlaceholder")}
              maxLength={160}
              rows={3}
              className="w-full px-3 py-2.5 bg-th-surface border border-th-border rounded-xl text-sm text-th-text placeholder-th-muted resize-none focus:outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all"
            />
            <p className="text-xs text-th-muted text-right font-mono">{bio.length}/160</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-th-text">{t("editProfile.travelStyle")}</p>
              <p className="text-[11px] font-mono text-th-muted uppercase tracking-wider">
                {t("editProfile.selected", { count: selectedStyles.length })}
              </p>
            </div>

            <div className="space-y-4">
              {Object.entries(TRAVEL_STYLES).map(([category, styles]) => (
                <div key={category}>
                  <p className="text-[11px] font-mono uppercase tracking-wider text-th-muted mb-2">
                    {STYLE_LABELS[category]}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {styles.map((style) => {
                      const active = selectedStyles.includes(style);
                      return (
                        <button
                          key={style}
                          onClick={() => toggleStyle(style)}
                          className={`text-xs font-mono px-3 py-1.5 rounded-lg border transition-all ${
                            active
                              ? "bg-th-accent-dim border-th-accent-border text-th-accent"
                              : "bg-th-surface border-th-border text-th-muted hover:text-th-text-sec hover:border-th-text-sec"
                          }`}
                        >
                          {style}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {missingCategories.length > 0 && selectedStyles.length > 0 && (
            <p className="text-xs text-amber-500 font-mono">
              {t("editProfile.stillNeed", { categories: missingCategories.join(", ") })}
            </p>
          )}

          {error && (
            <p className="text-sm text-th-danger bg-th-danger-bg border border-th-danger-border px-3 py-2 rounded-xl">{error}</p>
          )}

          <button
            onClick={handleSave}
            disabled={saving || saved || missingCategories.length > 0}
            className="w-full py-3.5 bg-th-accent text-th-bg rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saved ? (
              <>
                <Check size={14} />
                {t("editProfile.saved")}
              </>
            ) : saving ? (
              <>
                <Loader size={14} className="animate-spin" />
                {t("editProfile.saving")}
              </>
            ) : (
              t("editProfile.save")
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditProfile;
